import { useState, useEffect, useCallback } from 'react';
import { remoteConfigService } from '../lib/firebase/config';
import { FeatureFlagName, FeatureFlags } from '@curbside-quest/shared';
import { conditionalLog, conditionalWarn, conditionalError, initializeLogger, updateLoggingState } from '../lib/utils/logger';

interface UseFeatureFlagReturn {
  isEnabled: (flagName: FeatureFlagName) => boolean;
  getValue: <T>(flagName: string, defaultValue: T) => T;
  isLoading: boolean;
  error: Error | null;
  refresh: () => Promise<void>;
}

/**
 * React hook for accessing feature flags with environment-specific strategies:
 * - Local Development: Environment variables + defaults only
 * - Staging/Production: Remote Config + environment variable overrides + defaults
 */
export function useFeatureFlag(): UseFeatureFlagReturn {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const [lastFetch, setLastFetch] = useState<number>(0);

  // Initialize feature flag system using RemoteConfigService
  useEffect(() => {
    const envInfo = remoteConfigService.getEnvironmentInfo();
    conditionalLog('🔧 Feature flags: Initializing with environment info:', envInfo);

    if (envInfo.isLocalDevelopment) {
      // Local development: Use environment variables only
      conditionalLog('🏁 Feature flags: Local development mode (environment variables + defaults only)');
      setIsLoading(false);
      setError(null);
      setLastFetch(Date.now());

      // Initialize the logger for local development
      initializeLogger((flag: FeatureFlagName) => getDefaultValue(flag));

      return;
    }

    // Staging/Production: Use Remote Config service
    let mounted = true;

    const initializeRemoteConfig = async () => {
      try {
        setIsLoading(true);
        setError(null);

        conditionalLog('🔧 Feature flags: Waiting for Remote Config service...');

        // Wait for Remote Config service to be initialized
        let attempts = 0;
        const maxAttempts = 50; // 5 seconds max wait

        while (!remoteConfigService.isAvailable() && !remoteConfigService.getError() && attempts < maxAttempts) {
          if (remoteConfigService.isInitializing()) {
            conditionalLog(`🔧 Remote Config service is initializing... (attempt ${attempts + 1}/${maxAttempts})`);
          }
          await new Promise(resolve => setTimeout(resolve, 100));
          attempts++;
        }

        const serviceError = remoteConfigService.getError();
        if (serviceError) {
          throw serviceError;
        }

        if (!remoteConfigService.isAvailable()) {
          throw new Error(`Remote Config service not available after ${attempts * 100}ms`);
        }

        conditionalLog('✅ Remote Config service is available, fetching values...');

        // Fetch and activate Remote Config
        await remoteConfigService.fetchAndActivate();

        if (mounted) {
          setLastFetch(Date.now());
          setIsLoading(false);
          conditionalLog('✅ Feature flags: Remote Config initialized successfully');

          // Initialize the logger with the feature flag service
          initializeLogger((flag: FeatureFlagName) => {
            try {
              const remoteValue = remoteConfigService.getValue(flag);
              return remoteValue.asBoolean();
            } catch {
              return getDefaultValue(flag);
            }
          });
        }
      } catch (err) {
        conditionalError('❌ Feature flags: Remote Config initialization failed:', err);
        if (mounted) {
          setError(err instanceof Error ? err : new Error('Failed to initialize Remote Config'));
          setIsLoading(false);
        }
      }
    };

    // Add listener for Remote Config service state changes
    const removeListener = remoteConfigService.addListener(() => {
      if (mounted) {
        const envInfo = remoteConfigService.getEnvironmentInfo();
        if (envInfo.hasError) {
          setError(new Error(envInfo.error || 'Remote Config service error'));
          setIsLoading(false);
        } else if (envInfo.isInitialized) {
          setError(null);
          // Don't set loading to false here, wait for fetchAndActivate
        }
      }
    });

    initializeRemoteConfig();

    return () => {
      mounted = false;
      removeListener();
    };
  }, []);

  /**
   * Check if a feature flag is enabled
   * Strategy depends on environment:
   * - Local: Environment Variables > Remote Config > Defaults
   * - Staging/Production: Remote Config > Defaults (env overrides disabled by default)
   */
  const isEnabled = useCallback((flagName: FeatureFlagName): boolean => {
    conditionalLog(`🔧 useFeatureFlag.isEnabled(${flagName}) called`);

    try {
      // 1. Check environment variable override (only in dev or if explicitly enabled)
      const envOverride = getEnvironmentOverride(flagName);
      if (envOverride !== undefined) {
        conditionalLog(`🏁 Feature flag ${flagName}: env override = ${envOverride}`);
        return envOverride;
      }

      // 2. Use Remote Config service to get value
      const remoteValue = remoteConfigService.getValue(flagName);
      const boolValue = remoteValue.asBoolean();
      const stringValue = remoteValue.asString();
      const source = remoteValue.getSource();

      conditionalLog(`🏁 Feature flag ${flagName}: ${source} = ${boolValue} (string: "${stringValue}")`);

      return boolValue;
    } catch (err) {
      conditionalWarn(`Feature flag ${flagName} error, using default:`, err);
      const defaultValue = getDefaultValue(flagName);
      conditionalLog(`🏁 Feature flag ${flagName}: error fallback = ${defaultValue}`);
      return defaultValue;
    }
  }, [lastFetch]);

  /**
   * Get a typed value for any configuration parameter
   * In local development: Environment Variables > Defaults (skip Remote Config)
   */
  const getValueTyped = useCallback(<T>(flagName: string, defaultValue: T): T => {
    try {
      // Check environment variable first
      const envValue = getEnvironmentVariable(`VITE_FEATURE_${flagName.toUpperCase()}`);
      if (envValue !== undefined) {
        // Try to parse the environment value to match the expected type
        if (typeof defaultValue === 'boolean') {
          return (envValue.toLowerCase() === 'true') as T;
        } else if (typeof defaultValue === 'number') {
          const parsed = parseFloat(envValue);
          return (isNaN(parsed) ? defaultValue : parsed) as T;
        } else {
          return envValue as T;
        }
      }

      // In local development, skip Remote Config and use defaults
      const envInfo = remoteConfigService.getEnvironmentInfo();
      if (envInfo.isLocalDevelopment) {
        return defaultValue;
      }

      // Get from Remote Config service
      const remoteValue = remoteConfigService.getValue(flagName as FeatureFlagName);

      if (typeof defaultValue === 'boolean') {
        return remoteValue.asBoolean() as T;
      } else if (typeof defaultValue === 'number') {
        return remoteValue.asNumber() as T;
      } else {
        return remoteValue.asString() as T;
      }
    } catch (err) {
      conditionalWarn(`Failed to get config value ${flagName}, using default:`, err);
      return defaultValue;
    }
  }, [lastFetch]);

  /**
   * Manually refresh Remote Config
   * - Local development: No-op (just triggers re-render)
   * - Staging/Production: Fetches latest Remote Config values
   */
  const refresh = useCallback(async (): Promise<void> => {
    const envInfo = remoteConfigService.getEnvironmentInfo();

    conditionalLog('🔧 Feature flags refresh requested. Environment info:', envInfo);

    if (envInfo.isLocalDevelopment) {
      conditionalLog('🏁 Feature flags refresh: Local development mode (no Remote Config to refresh)');
      setLastFetch(Date.now());
      return;
    }

    conditionalLog('🔧 Refreshing Remote Config (force refresh)...');
    try {
      setIsLoading(true);
      setError(null);

      await remoteConfigService.forceRefresh();
      setLastFetch(Date.now());
      conditionalLog('✅ Remote Config refreshed successfully');

      // Update the logger state after refresh
      updateLoggingState();
    } catch (err) {
      conditionalError('❌ Remote Config refresh failed:', err);
      setError(err instanceof Error ? err : new Error('Failed to refresh Remote Config'));
    } finally {
      setIsLoading(false);
    }
  }, []);

  return {
    isEnabled,
    getValue: getValueTyped,
    isLoading,
    error,
    refresh
  };
}

/**
 * Get environment variable override for a flag
 * Only applies in local development unless explicitly enabled
 */
function getEnvironmentOverride(flagName: FeatureFlagName): boolean | undefined {
  // Check if environment overrides should be used
  const allowEnvOverrides = import.meta.env.DEV || import.meta.env.VITE_ALLOW_ENV_OVERRIDES === 'true';

  if (!allowEnvOverrides) {
    conditionalLog(`🔧 Environment overrides disabled for ${flagName} (staging/production mode)`);
    return undefined;
  }

  const envVarName = `VITE_FEATURE_${flagName}`;
  const envValue = getEnvironmentVariable(envVarName);

  if (envValue !== undefined) {
    const boolValue = envValue.toLowerCase() === 'true';
    conditionalLog(`🔧 Environment override found for ${flagName}: ${envVarName}=${envValue} → ${boolValue}`);
    return boolValue;
  }

  return undefined;
}

/**
 * Get environment variable value
 */
function getEnvironmentVariable(name: string): string | undefined {
  return import.meta.env[name];
}

/**
 * Get default value for a flag based on environment
 */
function getDefaultValue(flagName: FeatureFlagName): boolean {
  const defaults: FeatureFlags = {
    AI_ANALYSIS_ENABLED: import.meta.env.DEV || false,
    AI_SIMULATE_FAILURE: false,
    AI_SERVICE_UNAVAILABLE: false,
    ADVANCED_SEARCH_ENABLED: true,
    PREMIUM_FEATURES_ENABLED: true,
    DEBUG_MODE_ENABLED: import.meta.env.DEV || false,
    MAINTENANCE_MODE: false,
    CONSOLE_LOGGING_ENABLED: import.meta.env.DEV || false,
    ACTION_COMPONENTS_ENABLED: true,
    GOOGLE_ADS_ENABLED: false,
    GAMIFICATION_ENABLED: false
  };

  const defaultValue = defaults[flagName];

  // Debug logging in development
  const envInfo = remoteConfigService.getEnvironmentInfo();
  if (import.meta.env.DEV && envInfo.isLocalDevelopment) {
    conditionalLog(`🏁 Feature flag ${flagName}: using default value ${defaultValue} (local development mode)`);
  }

  return defaultValue;
}